import * as v from 'valibot';

import { logEmptyResponseToSentry, logValidationErrorsToSentry } from '../lib';

import { EValidationIssueSeverity } from './validation-issue-severity.enum';

import type {
  DataWithValidation,
  ResponseResult,
  ValidationIssue,
} from './validation.types';

const severityRegex = new RegExp(
  `^@(${Object.values(EValidationIssueSeverity).join('|')}):`,
  'i',
);

/** An issue's severity from its `@WARNING:`/`@ERROR:` prefix; unprefixed is ERROR. */
export const issueSeverity = (issue: {
  message: string;
}): EValidationIssueSeverity =>
  ((issue.message.match(severityRegex) ?? [])[1]?.toUpperCase() as
    EValidationIssueSeverity | undefined) ?? EValidationIssueSeverity.ERROR;

/** The rule REST drops an item by, shared with the live entity-frame path. */
export const hasErrorIssue = (
  issues: readonly { message: string }[] | undefined,
): boolean =>
  issues?.some(
    issue => issueSeverity(issue) === EValidationIssueSeverity.ERROR,
  ) ?? false;

export const transformIssues = <
  R extends object | object[],
  S extends v.SafeParseResult<v.GenericSchema<ResponseResult<R>>> =
    v.SafeParseResult<v.GenericSchema<ResponseResult<R>>>,
  T = R extends object[] ? R[number] : R,
>(
  validationResult: S,
  keyTransform?: (
    value: Partial<T>,
    issue: v.BaseIssue<unknown>,
  ) => string | undefined,
): ValidationIssue[] =>
  validationResult.issues?.map(issue => {
    // Safely extract value from path
    const value = (
      issue.path?.[1]?.type === 'array'
        ? issue.path?.[1]?.value
        : issue.path?.[0]?.value
    ) as Partial<T>;
    const key =
      keyTransform && keyTransform((value || {}) satisfies Partial<T>, issue);
    const path = issue.path?.map(p => p.key) ?? [];
    const pathKey = issue.path?.map(p => p.key).join('.') || 'root';
    const message = issue.message satisfies
      `${string}` | `@${EValidationIssueSeverity}:${string}`;

    const severity = issueSeverity(issue);

    return {
      path,
      pathKey,
      severity,
      message: message.replace(severityRegex, '').trim(),
      key: key || pathKey,
    };
  }) ?? [];

/**
 * Validates and transforms API response with error filtering for array data.
 * Items with ERROR severity validation issues are filtered out from the result.
 * ERROR-level issues are logged to Sentry for monitoring.
 *
 * @param schema - Valibot schema for response validation
 * @param response - Raw API response
 * @param keyTransform - Optional function to extract a key from each item for validation issues
 * @param endpointName - Optional endpoint name for Sentry logging (recommended)
 * @returns Transformed response with validation metadata
 *
 * @example
 * transformResponse: (response) =>
 *   validateAndTransformResponse<ActiveCase[]>(
 *     activeCasesResponseSchema,
 *     response,
 *     value => value?.label,
 *     'activeCases'
 *   )
 */
export const validateAndTransformResponse = <
  TSchema extends v.GenericSchema<ResponseResult<object[] | object>>,
  TData extends v.InferInput<TSchema>['data'] = v.InferInput<TSchema>['data'],
>(
  schema: TSchema,
  response: ResponseResult<TData>,
  keyTransform?: (
    value: TData extends object[] ? Partial<TData[number]> : Partial<TData>,
    issue: v.BaseIssue<unknown>,
  ) => string | undefined,
  endpointName?: string,
  disabled?: boolean,
): DataWithValidation<TData> => {
  // SpecRef: ARCH-005
  // Reference: docs/decisions/ARCH-005-ui-error-detection-resilience.md#10-dedicated-sentry-telemetry-for-empty-responses
  // Fires before the disabled guard so aircraft/hubs endpoints are covered even when schema validation is off.
  if (
    Array.isArray(response.data) &&
    response.data.length === 0 &&
    endpointName
  ) {
    logEmptyResponseToSentry(endpointName);
  }

  if (disabled) {
    return {
      data: response.data,
      generatedAt: response.dataGeneratedAt,
      cursor: response.cursor,
      validation: {
        isValid: true,
        issues: [],
      },
    };
  }
  const validationResult = v.safeParse(schema, response);
  const issues = transformIssues<TData>(
    validationResult as v.SafeParseResult<
      v.GenericSchema<ResponseResult<TData>>
    >,
    keyTransform as
      | ((
          value: Partial<TData extends object[] ? TData[number] : TData>,
          issue: v.BaseIssue<unknown>,
        ) => string | undefined)
      | undefined,
  );

  // Filter ERROR-level issues
  const errorIssues = issues.filter(
    issue => issue.severity === EValidationIssueSeverity.ERROR,
  );
  const errorIndexes = errorIssues.map(issue => Number(issue.path[1]));

  // Log ERROR-level issues to Sentry
  if (errorIssues.length > 0 && Array.isArray(response.data)) {
    logValidationErrorsToSentry(
      endpointName || 'unknown',
      errorIssues,
      response.data.length,
      errorIndexes.length,
    );
  }

  const data =
    Array.isArray(response.data) && errorIndexes.length > 0
      ? (response.data?.filter(
          (_, index) => !errorIndexes.includes(index),
        ) as TData)
      : response.data;

  // Derive `cursor` from the validated output, not the raw response — a
  // malformed cursor (e.g. non-string) must be dropped rather than
  // propagated into the cursor store. `validationResult.output` is typed
  // `unknown` across the safeParse result union, hence the cast. Valibot's
  // `output` keeps the raw (invalid) value in place for a field that fails
  // typing rather than stripping it, so the explicit `typeof` check below is
  // what actually does the dropping — reading `output.cursor` alone would
  // still surface a non-string value as-is.
  const outputCursor = (validationResult.output as { cursor?: unknown } | null)
    ?.cursor;
  const validatedCursor =
    typeof outputCursor === 'string' ? outputCursor : undefined;

  return {
    data,
    generatedAt: response.dataGeneratedAt,
    cursor: validatedCursor,
    validation: {
      isValid: validationResult.success,
      issues,
    },
  };
};
